import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import Logo from './Logo';
import { useContent } from '../hooks/useContent';

const NAV = [
  { to: '/', key: 'home', label: 'Home' },
  { to: '/team', key: 'team', label: 'Team' },
  { to: '/advisory-services', key: 'advisory', label: 'Advisory Services' },
  { to: '/investment-solutions', key: 'investment', label: 'Investment Solutions' },
];

const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const { t } = useContent('site');

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-40 transition-all duration-500 ${scrolled ? 'py-3 backdrop-blur-md bg-cream/90 border-b border-navy/10' : 'py-6 bg-transparent'}`}
    >
      <div className="max-w-[1440px] mx-auto px-8 md:px-14 flex items-center justify-between">
        <Link to="/" className="transition-opacity hover:opacity-80" data-testid="nav-logo-link">
          <Logo size="small" />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden md:flex items-center gap-2" data-testid="desktop-nav">
          {NAV.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === '/'}
              data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
              className={({ isActive }) =>
                `font-sans text-[11px] tracking-[0.3em] px-4 py-2 rounded-full transition-colors duration-300 ${
                  isActive ? 'text-navy bg-navy/10' : 'text-navy hover:bg-navy/5'
                }`
              }
            >
              {t(`nav.${item.key}`).toUpperCase()}
            </NavLink>
          ))}
          <Link
            to="/contact"
            data-testid="nav-contact-btn"
            className="ml-3 font-sans text-[11px] tracking-[0.3em] text-navy px-5 py-2.5 border border-navy/40 rounded-full hover:bg-navy hover:text-cream transition-colors duration-300"
          >
            {t('nav.contact').toUpperCase()}
          </Link>
        </nav>

        {/* Mobile toggle */}
        <button
          onClick={() => setMobileOpen(o => !o)}
          className="md:hidden font-sans text-[11px] tracking-[0.3em] text-navy px-3 py-2 border border-navy/30 rounded-full"
          data-testid="mobile-nav-toggle"
          aria-label="Toggle menu"
        >
          {mobileOpen ? 'CLOSE' : 'MENU'}
        </button>
      </div>

      {/* Mobile menu (dropdown panel, page remains visible below) */}
      {mobileOpen && (
        <div
          className="md:hidden absolute left-0 right-0 top-full bg-cream/98 backdrop-blur-md border-t border-navy/10 shadow-lg"
          data-testid="mobile-nav-panel"
        >
          <div className="max-w-[1440px] mx-auto px-8 py-6 flex flex-col gap-1">
            {NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                data-testid={`mobile-nav-link-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                className={({ isActive }) =>
                  `font-serif text-2xl py-3 border-b border-navy/10 ${isActive ? 'text-navy' : 'text-navy/80'}`
                }
              >
                {t(`nav.${item.key}`)}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="mobile-nav-contact"
              className="mt-4 font-sans text-[11px] tracking-[0.3em] text-navy px-5 py-3 border border-navy/40 rounded-full inline-block w-fit"
            >
              {t('nav.contact').toUpperCase()}
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
